// =============================================================================
// addDiscoverySchoolAsTarget.ts (v0.2.32)
// =============================================================================
//
// Converts a DiscoverySchool (from the bundled discovery dataset) into a
// TargetSchool + ScorecardSnapshot and writes it into the applicant's
// target_schools subcollection — the same machinery the Profile tab uses.
//
// Because the discovery dataset already carries all the ScorecardSnapshot
// fields, this needs NO College Scorecard API call: the snapshot is built
// directly from the discovery record.
// =============================================================================

import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import type { TargetSchool, ScorecardSnapshot } from '@scholarship-app/types';
import type { DiscoverySchool } from './schoolDiscoveryTypes';
import { loadDiscoveryDataset } from './schoolDiscovery';

function genId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

/** ownership code -> ScorecardSnapshot ownership union */
function ownershipLabel(o: number | null): ScorecardSnapshot['ownership'] {
  if (o === 1) return 'public';
  if (o === 2) return 'private_nonprofit';
  if (o === 3) return 'private_for_profit';
  return null;
}

/** ownership code -> TargetSchool.school_type union.
 *  TargetSchool distinguishes in-state vs out-of-state public; from the
 *  discovery data alone we can't know the applicant's state relative to the
 *  school, so public schools default to public_in_state (the user can change
 *  it in the Profile editor). */
function targetSchoolType(o: number | null): TargetSchool['school_type'] {
  return o === 1 ? 'public_in_state' : 'private';
}

/** Build a ScorecardSnapshot from a discovery school record. */
export function snapshotFromDiscovery(s: DiscoverySchool): ScorecardSnapshot {
  // SAT 25/75 in the discovery data are split reading/math; the snapshot wants
  // a single composite 25/75. Sum reading + math when both are present.
  const sat25 =
    s.satReading25 != null && s.satMath25 != null
      ? s.satReading25 + s.satMath25
      : null;
  const sat75 =
    s.satReading75 != null && s.satMath75 != null
      ? s.satReading75 + s.satMath75
      : null;

  return {
    scorecard_id: s.id,
    name: s.name,
    website_url: s.url,
    city: s.city,
    state: s.state,
    zip: s.zip,
    region: s.region != null ? String(s.region) : null,
    ownership: ownershipLabel(s.ownership),
    religious_affiliation: s.religious != null ? String(s.religious) : null,
    main_campus: s.mainCampus != null ? s.mainCampus === 1 : null,
    undergrad_enrollment: s.size,
    pct_women: s.pctWomen,
    pct_men: s.pctMen,
    pct_white: s.pctWhite,
    pct_black: s.pctBlack,
    pct_hispanic: s.pctHispanic,
    pct_asian: s.pctAsian,
    admission_rate: s.admitRate,
    sat_25: sat25,
    sat_75: sat75,
    act_25: s.act25,
    act_75: s.act75,
    in_state_tuition: s.tuitionIn,
    out_of_state_tuition: s.tuitionOut,
    room_board_on_campus: s.roomBoard,
    avg_net_price: s.netPrice,
    graduation_rate_6yr: s.gradRate,
    median_earnings_10yr: s.earnings10yr,
  };
}

/**
 * Whether this discovery school is already a target school for the applicant.
 * Matches on scorecard_id.
 */
export async function findExistingTargetSchoolId(
  db: Firestore,
  householdId: string,
  applicantId: string,
  scorecardId: number
): Promise<string | null> {
  const ref = collection(
    db, 'households', householdId, 'applicants', applicantId, 'target_schools'
  );
  const snap = await getDocs(ref);
  for (const d of snap.docs) {
    const t = d.data() as TargetSchool;
    if (t.scorecard_id === scorecardId && !t.archived) return t.school_id;
  }
  return null;
}

/**
 * Add a discovery school as a target school. Returns the new school_id.
 * Builds the ScorecardSnapshot from the discovery record — no API call.
 */
export async function addDiscoverySchoolAsTarget(
  db: Firestore,
  householdId: string,
  applicantId: string,
  s: DiscoverySchool
): Promise<string> {
  const id = genId('school');
  const ref = doc(
    db, 'households', householdId, 'applicants', applicantId, 'target_schools', id
  );
  const snapshot = snapshotFromDiscovery(s);

  const target: Omit<TargetSchool, 'created_at' | 'updated_at'> = {
    school_id: id,
    school_name: s.name,
    school_short_name: '',
    tier: 'backup',
    rank: 1,
    school_type: targetSchoolType(s.ownership),
    city: s.city ?? '',
    state: s.state ?? '',
    admission_deadlines: {
      early_action: null,
      early_decision: null,
      regular_decision: null,
      rolling: false,
    },
    applied: false,
    admitted: false,
    decision_date: null,
    notes: 'Added from School Discovery.',
    institutional_aid_link: null,
    honors_program_link: null,
    archived: false,
    scorecard_id: s.id,
    scorecard_data: snapshot,
    scorecard_fetched_at: new Date().toISOString(),
  };

  await setDoc(ref, {
    ...target,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return id;
}

/**
 * Look up a school in the bundled discovery dataset by its College Scorecard
 * id and return a ScorecardSnapshot for it — no API call. Used by the Profile
 * target-school editor (v0.2.33) so adding a school there draws from the same
 * bundled data the Schools tab uses. Returns null if the id isn't found.
 */
export async function getDiscoverySnapshotById(
  scorecardId: number
): Promise<ScorecardSnapshot | null> {
  const dataset = await loadDiscoveryDataset();
  const school = dataset.schools.find((s) => s.id === scorecardId);
  return school ? snapshotFromDiscovery(school) : null;
}
